<template>
  <section class="position-relative pt-4 select-items px-3" v-if="!isLoading && !initialLoading">
    <b-row class="justify-content-md-center">
      <b-col lg="10" class="pl-sm-3 pr-sm-3 pl-0 pr-0">
        <div v-if="orders.length">
          <template v-for="order in orders">
            <div class="orders-container" :key="'order-' + order.id.toString()">
              <div v-if="!isSubscriptionOnly || debug" class="card-header">
                <div class="top-container d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center">
                    <span class="top-container-text order_number">#{{order.display_order_id || order.order_id}}</span>
                    <span class="top-container-text order_date">
                      <span class="separator">|</span>
                      {{getShortHumanReadableDate(order.created)}}
                    </span>
                    <span class="separator">|</span>
                    <span class="badge order-status payment_status active" :class="getPaymentStatusClass(order)">{{getPaymentStatus(order)}}</span>
                    <template v-if="debug">
                      <span class="separator"></span>
                      <span class="top-container-text">{{getGatewayString(order)}}</span>
                    </template>
                  </div>
                </div>
              </div>
              <div class="order-container">
                <div
                  :class="{'clickable-order-container': !useIntentButtons}"
                  v-for="item in order.items"
                  :key="'item-' + item.id.toString()"
                  @click="selectItemArea($event, item)"
                >
                  <div class="d-block d-md-none">
                    <template v-if="item.campaign_product.show_next_bill_price">
                      <template v-if="isSubscriptionActive(item)">
                        <div class="item-img-top-text" v-if="item.next_bill_date">
                          Next {{item.is_shippable ? 'ship' : 'bill'}} date : {{getShortHumanReadableUTCDate(item.next_bill_date)}}
                        </div>
                        <div class="item-img-top-text" v-if="isSubscriptionPauseScheduled(item)">
                          Paused until {{getShortHumanReadableUTCDate(item.pause_resume_date)}}
                        </div>
                      </template>
                      <div class="item-img-top-text" v-else-if="isSubscriptionCancelScheduled(item)">
                        Cancel on {{getShortHumanReadableUTCDate(item.cancel_date)}}
                      </div>
                      <div class="item-img-top-text" v-else-if="isSubscriptionComplete(item)">
                        No future {{item.is_shippable ? 'shipments' : 'billing'}}
                      </div>
                    </template>
                  </div>
                  <div class="order-item-container pl-0" :class="{selected: isSelected(item)}">
                    <div class="item-image">
                      <img :src="getImageUrl(item)" @error="setAltImg($event, item)" alt="" />
                    </div>
                    <div class="order-item-info">
                      <div class="item-detail-text">
                        <span class="item-name" v-html="getName(item)"></span>
                        <span v-if="debug"> ({{item.campaign_product.campaign_product_id}})</span>
                        <div v-if="getVariant(item)" class="item-variants">
                          <div v-for="option of getVariant(item).options" :key="'variant-option-' + option.id">
                            <span v-if="!option.is_deleted">{{option.name}}: {{option.value}}</span>
                          </div>
                          <span v-if="debug">Variant ID: {{getVariant(item).variant_id}}</span>
                        </div>
                        <div v-else-if="getBundledVariants(item)" class="item-variants">
                          <div v-for="bundledVariant of getBundledVariants(item)" :key="'variant-' + bundledVariant.variant.id">
                            <div v-for="option of bundledVariant.variant.options" :key="'variant-option-' + option.id">
                              <span v-if="!option.is_deleted">
                                <span v-if="bundledVariant.variant.options.length > 2">{{option.name}}: </span>
                                {{option.value}} x {{bundledVariant.quantity}}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="notranslate item-detail-text">
                        <div :class="{'original-price': isReplaced(item)}">
                          <span v-if="isDiscounted(item)" class="original-price">{{getOriginalPrice(item) | currency(order.currency)}}</span> {{getPrice(item) | currency(order.currency)}}
                          <span v-if="getBundleQuantity(item) > 1">&nbsp;({{getBundleQuantity(item)}} x {{getBundleUnitPrice(item) | currency(order.currency)}})</span>
                        </div>
                      </div>
                      <div>
                        <template v-if="isRecurring(item)">
                          <div class="item-detail-text">
                            <span class="mr-2">Subscription</span>
                            <span class="badge order-status" :class="getItemStatusClass(item)">{{getItemStatus(item)}}</span>
                          </div>
                          <template v-if="debug && item.subscription_id">
                            <div class="item-detail-text">
                              Subscription ID : {{item.subscription_id}}
                            </div>
                            <div class="item-detail-text">
                              Billing Cycle : {{item.billing_cycle}}
                            </div>
                          </template>
                          <template v-if="item.campaign_product.show_next_bill_price">
                            <template v-if="isSubscriptionActive(item)">
                              <div class="item-detail-text" v-if="item.next_bill_date">
                                Next {{item.is_shippable ? 'ship' : 'bill'}} date : {{getShortHumanReadableUTCDate(item.next_bill_date)}}
                              </div>
                              <div class="item-detail-text" v-if="isSubscriptionPauseScheduled(item)">
                                Paused until {{getShortHumanReadableUTCDate(item.pause_resume_date)}}
                              </div>
                            </template>
                            <div class="item-detail-text" v-else-if="isSubscriptionCancelScheduled(item)">
                              Cancel on {{getShortHumanReadableUTCDate(item.cancel_date)}}
                            </div>
                            <div class="item-detail-text" v-else-if="isSubscriptionComplete(item)">
                              No future {{item.is_shippable ? 'shipments' : 'billing'}}
                            </div>
                          </template>
                        </template>
                        <template v-else>
                          <div class="item-detail-text">One-time purchase</div>
                        </template>
                      </div>
                      <div class="item-detail-text" v-if="hasPendingCharge(item)">
                        Pending charge for {{item.amount_pending | currency(order.currency)}}
                      </div>
                      <div class="item-detail-text" v-if="showFulfillment(item)">
                        <span class="mr-2">Shipment</span>
                        <span class="badge order-status" :class="getItemFulfillmentStatusClass(item)">{{getItemFulfillmentStatus(item)}}</span>
                      </div>
                    </div>
                    <div class="d-none d-md-block item-select-btn-container">
                      <SelectItemButtons
                        :selected="isSelected(item)"
                        :item-select-label="itemSelectLabel"
                        :use-intents="useIntentButtons"
                        :intents="item['actions']"
                        :subscription="isSubscriptionOnly && !isItemStatus"
                        @select="selectItemButton(item, ...arguments)"
                        @viewSubscriptionOrders="selectSubscription(item)"
                      ></SelectItemButtons>
                    </div>
                  </div>
                  <div v-if="!noselect" class="d-block d-md-none order-item-container p-0">
                    <hr class="m-0"/>
                    <div class="card-padding item-select-btn-container">
                      <SelectItemButtons
                        :selected="isSelected(item)"
                        :item-select-label="itemSelectLabel"
                        :use-intents="useIntentButtons"
                        :intents="item['actions']"
                        :subscription="isSubscriptionOnly && !isItemStatus"
                        @select="selectItemButton(item, ...arguments)"
                        @viewSubscriptionOrders="selectSubscription(item)"
                      ></SelectItemButtons>
                    </div>
                  </div>
                  <div class="d-none campaign-product-id">{{item.campaign_product.campaign_product_id}}</div>
                  <hr />
                </div>
              </div>
            </div>
            <OrderTotalsInfo v-if="!isSubscriptionOnly" :order="order" :key="'order-totals' + order.id.toString()" />
          </template>
        </div>
        <div v-else>
          <div class="centered-block">
            <div class="step-title">No items found</div>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row class="justify-content-center boxes select-item-btn mt-2 ml-sm-0 mr-sm-0 m-w-initial">
      <b-col v-if="session && session.can_undo && !noback" md="4" lg="3" :cols="multiple ? '6': '12'" class="back p-0">
        <button type="button" @click="goBack()" class="btn-base new-btn-outline-success input-choice-btn stroke-2x">Back</button>
      </b-col>
      <b-col v-if="multiple" md="4" lg="3" cols="6" class="p-0">
        <button type="button" @click="done()" class="btn-base input-choice-btn font-weight-normal stroke-2x" :disabled="!canContinue">{{label}}
        </button>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import axios from 'axios'
import {mapGetters, mapState} from 'vuex'
import {
  sortItems, getShortDate, getUTCShortDate, getItemStatus, getItemStatusClass, getItemImageUrl,
  getItemFulfillmentStatus, getItemFulfillmentStatusClass, showSubscription, showFulfillment,
  getShortHumanReadableDate, getPaymentStatus, getPaymentStatusClass, getShortHumanReadableUTCDate,
  isRecurring, setFallbackImageUrl, getItemIntents, getBundleUnitPrice, getBundleQuantity
} from '@/utils'
import {BillingCycleType, ItemStatus, InputType, PaymentSourceStrings} from '@/constants'
import OrderTotalsInfo from '@/components/OrderTotalsInfo'
const SelectItemButtons = () => import('./SelectItemButtons')

export default {
  name: 'SelectItems',
  components: {OrderTotalsInfo, SelectItemButtons},
  props: {
    input: Object,
    label: String,
    defaults: {
      type: Array,
      default: function() {
        return []
      }
    },
    noback: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    noselect: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    multiple: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    itemSelectLabel: {
      type: String,
      default: function() {
        return 'Select'
      }
    }
  },
  data: function() {
    return {
      getShortDate,
      getUTCShortDate,
      orders: [],
      selectedItemMap: {},
      canContinue: false,
      getShortHumanReadableDate,
      getShortHumanReadableUTCDate,
      initialLoading: true,
      selectedSubscriptionId: null,
      isSubscriptionOnly: false,
      isItemStatus: false
    }
  },
  computed: {
    ...mapState(['session', 'isLoading', 'campaign', 'debug', 'subscriptionOnly']),
    ...mapGetters(['subscriptionStatusLabels']),
    activeItemRequired: function() {
      return this.input && this.input.requires_active_item
    },
    isCheckout: function() {
      return this.input && (this.input.type === InputType.CheckoutConfirm)
    },
    useIntentButtons: function() {
      return !this.multiple && this.session && this.session.use_intents
    },
    additionalInputs: function() {
      return (this.session && this.session.additional_inputs) || []
    },
    allowedIntents: function() {
      let intents = []

      if (this.session) {
        if (this.useIntentButtons) {
          intents = this.session.intents
        } else {
          intents = this.additionalInputs.map((input) => input.type)
        }
      }

      return intents
    }
  },
  methods: {
    done: function() {
      if (this.canContinue) {
        let selectedItems = []

        this.orders.forEach(order => {
          order.items.forEach(item => {
            if (item.id in this.selectedItemMap) {
              selectedItems.push(item)
            }
          })
        })

        this.$emit('select', selectedItems) // done selecting items
      }
    },
    goBack: function() {
      if (this.selectedSubscriptionId) {
        this.selectedSubscriptionId = null
        this.isSubscriptionOnly = true
        this.init() // refresh
      } else {
        this.$emit('back') // go back to the previous step (ignore changes to item selection)
      }
    },
    selectSubscription: function(item) {
      if (item.subscription_id && !this.isItemStatus) {
        this.selectedSubscriptionId = item.subscription_id
        this.isSubscriptionOnly = false
        this.init() // refresh
      }
    },
    showFulfillment: function(item) {
      return !this.isSubscriptionOnly && showFulfillment(item)
    },
    showSubscription: function(item) {
      return showSubscription(item)
    },
    isRecurring: function(item) {
      return isRecurring(item)
    },
    isSubscriptionActive: function(item) {
      return (item.billing_cycle_type === BillingCycleType.Recurring) && item.can_cancel_subscription
    },
    isSubscriptionPauseScheduled: function(item) {
      return this.isSubscriptionActive(item) && (item.status !== ItemStatus.Paused) && item.pause_resume_date
    },
    isSubscriptionCancelScheduled: function(item) {
      return item.cancel_date
    },
    isSubscriptionComplete: function(item) {
      return item.status === ItemStatus.Complete
    },
    isReplaced: function(item) {
      return item.status === ItemStatus.Replaced
    },
    getPaymentStatus: function(order) {
      return getPaymentStatus(order, false)
    },
    getPaymentStatusClass: function(order) {
      return getPaymentStatusClass(order, false)
    },
    getItemStatus: function(item) {
      return getItemStatus(item, this.subscriptionStatusLabels, false)
    },
    getItemStatusClass: function(item) {
      return getItemStatusClass(item)
    },
    getItemFulfillmentStatus: function(item) {
      return getItemFulfillmentStatus(item)
    },
    getItemFulfillmentStatusClass: function(item) {
      return getItemFulfillmentStatusClass(item)
    },
    hasPendingCharge: function(item) {
      return parseFloat(item.amount_pending) > Number.EPSILON
    },
    selectItemArea: function(event, item) {
      event.stopPropagation()

      if (!this.useIntentButtons) {
        this.selectItemButton(item)
      }
    },
    getGatewayString: function(order) {
      let gateway = PaymentSourceStrings[order.payment_type]

      if (order.gateway_id) {
        gateway += ' gateway ' + order.gateway_id
      } else {
        gateway += ' payment method'
      }

      return gateway
    },
    selectItemButton: function(item, intent) {
      if (this.multiple) {
        if (item.id in this.selectedItemMap) {
          delete this.selectedItemMap[item.id]
        } else {
          this.selectedItemMap[item.id] = true
        }

        this.updateStatus()
      } else if (this.useIntentButtons) {
        this.$emit('select', [item], intent) // select intent button
      } else if (intent) {
        const input = this.additionalInputs.find((i) => i.type === intent)

        if (input) {
          this.$emit('selectItemInput', {item, input}) // select legacy additional intent input
        }
      } else {
        this.$emit('select', [item]) // select legacy item button (i.e. get help)
      }
    },
    isSelected: function(item) {
      return this.selectedItemMap && !!(item.id in this.selectedItemMap)
    },
    selectAll: function() {
      this.orders.forEach(order => {
        this.selectOrder(order)
      })

      this.updateStatus()
    },
    clearAll: function() {
      this.selectedItemMap = {}
      this.updateStatus()
    },
    selectOrder: function(order) {
      order.items.forEach(item => {
        this.selectedItemMap[item.id] = true
      })

      this.updateStatus()
    },
    clearOrder: function(order) {
      order.items.forEach(item => {
        if (item.id in this.selectedItemMap) {
          delete this.selectedItemMap[item.id]
        }
      })

      this.updateStatus()
    },
    getName: function(item) {
      return (this.isSubscriptionOnly && item.next_cycle_product) ? item.next_cycle_product.name : item.name
    },
    getVariant: function(item) {
      return (this.isSubscriptionOnly && item.next_variant) ? item.next_variant : item.variant
    },
    getBundledVariants: function(item) {
      return this.isSubscriptionOnly ? item.next_bundled_variants : item.bundled_variants
    },
    getOriginalPrice: function(item) {
      return this.isSubscriptionOnly ? item.next_recurring_price : item.price
    },
    getPrice: function(item) {
      return this.isSubscriptionOnly ? item.recurring_price : item.price
    },
    isDiscounted: function(item) {
      return this.isSubscriptionOnly &&
        ((parseFloat(this.getOriginalPrice(item)) - parseFloat(this.getPrice(item))) > Number.EPSILON)
    },
    getImageUrl: function(item) {
      return getItemImageUrl(item, 'medium', this.isSubscriptionOnly)
    },
    setAltImg: function(event, item) {
      setFallbackImageUrl(event, item, 'medium', true, this.isSubscriptionOnly)
    },
    updateStatus: function() {
      // update the select all checkbox
      let selectionCount = Object.keys(this.selectedItemMap).length
      this.canContinue = selectionCount !== 0

      this.orders = this.orders.slice() // force item statuses to be called
    },
    getBundleUnitPrice: function(item) {
      return getBundleUnitPrice(item, this.isSubscriptionOnly)
    },
    getBundleQuantity: function(item) {
      return getBundleQuantity(item, this.isSubscriptionOnly)
    },
    init: function() {
      this.$store.commit('busy')
      this.selectedItemMap = {}
      const sessionIntents = new Set(this.allowedIntents)

      // initialize the set of default selected items
      if (this.multiple) {
        this.defaults.forEach(item => {
          this.selectedItemMap[item.id] = true
        })
      }

      let params = {
        is_active: this.activeItemRequired,
        checkout: this.isCheckout
      }

      if (this.isSubscriptionOnly) {
        params = {
          latest_cycle: true,
          billing_cycle_type: BillingCycleType.Recurring
        }

        if (
          this.isItemStatus &&
          this.session &&
          this.session.selected_item &&
          this.session.selected_item.subscription_id
        ) {
          params['subscription_id'] = this.session.selected_item.subscription_id
        }
      } else if (this.selectedSubscriptionId) {
        params = {subscription_id: this.selectedSubscriptionId}
      }

      axios.get('/order_items/', {params: params}).then(result => {
        let items = result.data.results
        let lastOrderId = null
        let orders = []
        let order = {}
        let startIndex = 0
        const settings = this.campaign.portal_settings || {}

        sortItems(items)

        for (let i = 0; i < items.length; i++) {
          let orderId = items[i].order.id
          items[i]['actions'] = getItemIntents(items[i], settings).filter(intent => sessionIntents.has(intent))

          if (orderId !== lastOrderId) { // if this is the 1st item of a different order
            if (i > 0) {
              // if this is not the 1st item, then copy the items from the previous order
              order['items'] = items.slice(startIndex, i)
              orders.push(order)
            }

            lastOrderId = orderId
            startIndex = i
            order = Object.assign({}, items[i].order)
          }

          if (i === (items.length - 1)) { // if this is the last item
            order['items'] = items.slice(startIndex)
            orders.push(order)
          }
        }

        this.orders = orders
        this.updateStatus()
        this.$store.commit('ready')

        if (!items.length) {
          this.canContinue = true
        }
        this.initialLoading = false
      }, error => {
        console.error(error)
        this.$store.commit('critical_error', error)
        this.$store.commit('ready')
        this.initialLoading = false
      })
    }
  },
  created() {
    if (this.input) {
      this.isSubscriptionOnly = (this.input.type === InputType.SubscriptionItem)
      this.$store.commit('set_subscription_only', this.isSubscriptionOnly)
    } else {
      this.isSubscriptionOnly = this.subscriptionOnly
      this.isItemStatus = true
    }

    this.init()
  }
}
</script>

<style scoped>
</style>
